import './index.scss';
import ModalBase from '.';
import { useState } from 'react';
import { Alert, Button } from '@piggytechng/component-library';
import { PiUploadBold, PiTrashBold, PiFilePdfBold } from 'react-icons/pi';
import { OverideButtonStyle } from '../../helpers/utils';
import ModalExecuteAction from './ModalExecuteAction';
import useUSDRequest from '../../hooks/useUSDRequest';
import { USD_DOCUMENT_FIELDS } from '../../constants';
import { ModalReplaceUSDDocumentProps, USDDocumentField, USDFilePickerProps, USDPickedFile } from '../../types';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPES = 'image/png,image/jpeg,image/jpg,image/webp,image/heic,application/pdf';

const DOCUMENT_LABELS: Record<USDDocumentField, string> = {
  identity_url_links: 'Identity Document',
  utility_bill_url: 'Utility Bill',
  bank_statement_url: 'Bank Statement',
};

// Sent as a data URL — the endpoint accepts either raw base64 or the
// `data:<mime>;base64,` prefixed form.
const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

function FilePicker({ label, file, onPick, onClear, disabled }: USDFilePickerProps) {
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file after a clear
    if (!picked) return;

    if (picked.size > MAX_FILE_SIZE) {
      Alert.error('File is too large. Please upload a file under 10MB');
      return;
    }

    try {
      const base64 = await readAsBase64(picked);
      onPick({ base64, fileName: picked.name, isPdf: picked.type === 'application/pdf' });
    } catch (error) {
      Alert.error('We could not read that file. Please try again');
    }
  };

  return (
    <div className="usd-doc-upload">
      <label className="usd-doc-upload__label">{label}</label>

      {file ? (
        <div className="usd-doc-upload__preview">
          {file.isPdf ? (
            <div className="usd-doc-upload__pdf">
              <PiFilePdfBold size={28} />
              <span>{file.fileName}</span>
            </div>
          ) : (
            <img src={file.base64} alt={file.fileName} />
          )}
          <button type="button" onClick={onClear} disabled={disabled} className="usd-doc-upload__clear">
            <PiTrashBold size={14} /> Remove
          </button>
        </div>
      ) : (
        <label className="usd-doc-upload__dropzone">
          <PiUploadBold size={20} />
          <span>Click to select a file</span>
          <small>PNG, JPG or PDF &middot; max 10MB</small>
          <input type="file" accept={ACCEPTED_TYPES} onChange={handleChange} disabled={disabled} hidden />
        </label>
      )}
    </div>
  );
}

function ModalReplaceUSDDocument({
  modalIsOpen,
  setIsOpen,
  fileField,
  isDriversLicence = false,
}: ModalReplaceUSDDocumentProps) {
  const { updateUSDDocumentFile, isUpdatingDocumentFile } = useUSDRequest();
  const [front, setFront] = useState<USDPickedFile | null>(null);
  const [back, setBack] = useState<USDPickedFile | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // A back image only exists for a driver's licence identity document.
  const needsBack = fileField === USD_DOCUMENT_FIELDS.IDENTITY && isDriversLicence;

  const closeModal = () => {
    if (isUpdatingDocumentFile) return;
    setIsOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!front) {
      Alert.error('Please select a file to upload');
      return;
    }

    if (needsBack && !back) {
      Alert.error("Please upload both sides of the driver's licence");
      return;
    }

    // Hands over to the password confirmation step; the picker stays mounted so
    // a cancelled confirmation keeps the selected file.
    setShowConfirmation(true);
  };

  const confirmUpload = () => {
    if (!front) return;

    updateUSDDocumentFile(
      {
        fileField,
        base64: front.base64,
        fileName: front.fileName,
        ...(needsBack && back ? { backBase64: back.base64, backFileName: back.fileName } : {}),
      },
      () => {
        setShowConfirmation(false);
        setIsOpen(false);
      },
    );
  };

  return (
    <>
      <ModalBase modalIsOpen={modalIsOpen && !showConfirmation} setIsOpen={setIsOpen}>
        <div className="modal-processor modal-processor--sm-title">
          <div className="modal-processor__header">
            <h2>Replace {DOCUMENT_LABELS[fileField]}</h2>
            <button type="button" onClick={closeModal}>
              &times;
            </button>
          </div>

          <form className="modal-processor__body modal-processor__body--scroll" onSubmit={handleSubmit}>
            <FilePicker
              label={needsBack ? 'Front of licence' : DOCUMENT_LABELS[fileField]}
              file={front}
              onPick={setFront}
              onClear={() => setFront(null)}
              disabled={isUpdatingDocumentFile}
            />

            {needsBack && (
              <FilePicker
                label="Back of licence"
                file={back}
                onPick={setBack}
                onClear={() => setBack(null)}
                disabled={isUpdatingDocumentFile}
              />
            )}

            <p className="modal-processor__sub-text">
              The uploaded file replaces the document currently on the request.
            </p>

            <div className="modal-processor__actions">
              <button type="button" onClick={closeModal} className="modal-processor__actions-reject">
                Cancel
              </button>
              <Button
                {...OverideButtonStyle}
                className="modal-processor__actions-resolve"
                type="submit"
                disabled={isUpdatingDocumentFile || !front || (needsBack && !back)}
              >
                {isUpdatingDocumentFile ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </form>
        </div>
      </ModalBase>

      <ModalExecuteAction
        modalIsOpen={showConfirmation}
        setIsOpen={setShowConfirmation}
        text={`replace the ${DOCUMENT_LABELS[fileField].toLowerCase()} on this request?`}
        action={confirmUpload}
        actionColor="#1734CA"
        isLoading={isUpdatingDocumentFile}
      />
    </>
  );
}

export default ModalReplaceUSDDocument;
